import { DomainStore } from "./domainStore";
import { awaitSettledList } from "./awaitSettledList";
import { observable, runInAction, computed, action } from "mobx";
import {
  getEmails,
  getEmailsMinimal,
  MinimalEmail,
  updateEmail,
  updateEmailFields,
  updateEmailLatestVersion,
  applyEmailVersion,
  createEmail,
  sendTestEmail,
  getEmail,
  copyEmailToAnotherAccount,
  isEmailChangedError,
  loadedEmailRevision,
} from "./persistence/persistEmail";
import _ from "lodash";
import { EmailContentCreate } from "encharge-domain/lib/definitions/api/EmailContentCreate";
import { toastSuccess, toastError } from "../domain/errorHandling/toaster";
import {
  exportEmailEvents,
  getEmailsMetrics,
} from "./persistence/persistMetrics";
import { IFlow } from "components/Flows/FlowRoute";
import { integrationStateActive } from "encharge-domain/lib/helpers/constants";
import { StepBase } from "components/FlowEditor/Step/StepBase";
import { getEmailsForReadOnlyFlow } from "./persistence/persistFlow";
import { getUserTimezone } from "domain/helpers/asDateTime";

export interface CachedMetrics {
  metrics: ISingleEmailStats;
  periods: IEmailsStats["periods"];
  clicks: IEmailClicksMetrics;
}

type AllTimeMetricsByEmail = {
  [index: number]: { [index in IEmailStatsType]?: ISingleEmailStats };
};

export class EmailsStore {
  rootStore: DomainStore;
  constructor(rootStore: DomainStore) {
    this.rootStore = rootStore;

    // check to load metrics for the first time
    this.loadAllTimeMetricsByEmail();
  }
  initialized = false;

  @observable
  _emails: Map<IEmailContent["id"], IEmailContent> | undefined = undefined;

  @observable
  loadingEmails: boolean = false;

  @computed
  get emails() {
    if (this.initialized) {
      return this._emails;
    }

    // Get emails async
    runInAction(() => {
      this.loadingEmails = true;
    });
    this.initialized = true;
    let getEmailsFunc = () => getEmails();

    const readOnlyAuth = this.rootStore.permissionsStore.readOnlyAuthToken;
    const flowId = this.rootStore.flowsStore.currentFlowId;
    if (readOnlyAuth && flowId) {
      getEmailsFunc = () => getEmailsForReadOnlyFlow(flowId, readOnlyAuth);
    }

    getEmailsFunc()
      .then((emails) => {
        const emailsMap = _.map(emails, (email): [
          IEmailContent["id"],
          IEmailContent
        ] => [email.id, email]);
        runInAction(() => {
          this._emails = new Map(emailsMap);
        });
        runInAction(() => {
          this.loadingEmails = false;
        });
      })
      .catch((e) => {
        // Avoid showing errors to users who are logged out.
        toastError({
          message: "Error while loading your emails.",
          extra: e,
        });
        runInAction(() => {
          this.loadingEmails = false;
        });
      });
    // Until we retrieve them, emails are undefined
    return this._emails;
  }

  get emailsArray() {
    return this.emails ? Array.from(this.emails.values()) : [];
  }

  /**
   * Wait until the emails list has settled, so that writing one email into the
   * map is not silently thrown away. The rule, and the two ways of getting it
   * wrong that this file has already shipped, are in awaitSettledList.
   */
  /**
   * Note which writers go through this and which do not.
   *
   * getFullEmail and createEmail await it, because both can be the FIRST thing
   * that ever touches the store: a screen deep-linked to one email, or a save
   * from an editor opened directly. The map may not exist yet, so they have to
   * wait for it or their write is dropped.
   *
   * updateEmail, autoSaveEmail and applyVersion do not, and today that is safe
   * only because you cannot reach any of them without the email already being
   * loaded, which means the list already resolved. That is an invariant about
   * how the app is navigated rather than anything the code enforces. If one of
   * them ever becomes reachable cold, it needs this too.
   *
   * archiveEmail does not either, and it fails differently: it reads the
   * record out of the map to archive it, so `if (!this.emails) return` bails
   * BEFORE the API call. The others would go stale; this one silently does
   * nothing at all. Awaiting here would not fix that on its own, because a
   * rejected list leaves the map undefined for good and the getter never
   * retries; it would need to fetch the one email instead. Pre-existing, and
   * out of scope here, but it is the sharpest edge of the same invariant.
   */
  private async awaitEmailsMap() {
    await awaitSettledList({
      hasList: () => Boolean(this._emails),
      isLoading: () => this.loadingEmails,
      // reading the getter is what starts the list fetch, if nothing has yet,
      // and it sets loadingEmails synchronously before returning
      startLoading: () => {
        void this.emails;
      },
    });
  }

  /**
   * How many times each email has been written since the page loaded.
   *
   * A record fetched by `getFullEmail` is a SNAPSHOT. When the list is still in
   * flight the write of that snapshot has to wait for the map, and in that gap
   * the user can save the same email - `updateEmail` takes the `if
   * (this.emails)` branch and caches nothing, so there is no newer copy in the
   * map to defend itself. Without this counter the waiting snapshot lands on
   * top afterwards and the save is silently undone, and because the snapshot
   * looks complete (`html` + `editor.state`) every later `getFullEmail`
   * early-returns it - so the editor reopens on pre-edit content and
   * `BroadcastConfig` can PATCH it back to the server.
   *
   * Emails carry no `updatedAt`, so recency cannot be compared. Counting
   * writes is what is available, and it is enough: the snapshot only wins if
   * nothing was written for that id since it was taken.
   */
  private writeGeneration = new Map<IEmailContent["id"], number>();

  private markWritten(id: IEmailContent["id"] | undefined) {
    if (id === undefined) {
      return;
    }
    this.writeGeneration.set(id, (this.writeGeneration.get(id) || 0) + 1);
  }

  /**
   * The CAN-SPAM verdict each email was last saved or loaded with, as the API
   * answered: the one its sends read. "pending" holds them, and any save can
   * set it (an autosave too), so the editor shows it (EmailEditorWarnings).
   * Kept apart from the emails map, which does not exist until the whole list
   * has loaded and so would drop the verdict of a save made before that.
   */
  @observable
  canSpamVerdicts = new Map<
    IEmailContent["id"],
    IEmailContent["canSpamCompliance"]
  >();

  /** The verdict this email was last saved or loaded with; undefined when not known. */
  storedCanSpamVerdict(id: IEmailContent["id"] | undefined) {
    return id === undefined ? undefined : this.canSpamVerdicts.get(id);
  }

  private noteCanSpamVerdict(email: Partial<IEmailContent> | undefined) {
    const id = email?.id;
    const verdict = email?.canSpamCompliance;
    if (id === undefined || !verdict) return;
    runInAction(() => {
      this.canSpamVerdicts.set(id, verdict);
    });
  }

  /**
   * Put a freshly fetched email into the map without making the caller wait.
   *
   * Deliberately not awaited by `getFullEmail`: this is the load path for every
   * email editor, and awaiting the whole-account list here means a screen that
   * already has its email sits on a spinner until `GET /v1/emails` returns -
   * about 20s on a large account, against 1-2s before.
   */
  private async cacheFullEmail(
    fullEmail: IEmailContent,
    generationAtSnapshot: number
  ) {
    await this.awaitEmailsMap();
    runInAction(() => {
      const written = this.writeGeneration.get(fullEmail.id) || 0;
      if (written !== generationAtSnapshot) {
        // somebody saved while we were waiting; their copy is the real one
        return;
      }
      this._emails?.set(fullEmail.id, fullEmail);
    });
  }

  getEmailById(id: IEmailContent["id"]) {
    const emails = this.emails;
    if (!emails) {
      return undefined;
    }
    return emails.get(id);
  }

  // Lightweight {id, name}-only cache for screens that only need to render
  // an email picker (e.g. Email Metrics). Avoids pulling subject, fromEmail,
  // html, editor, etc. when none of it will be displayed.
  @observable
  _emailsMinimal:
    | Map<IEmailContent["id"], MinimalEmail>
    | undefined = undefined;

  minimalInitialized = false;

  @computed
  get emailsMinimal(): Map<IEmailContent["id"], MinimalEmail> | undefined {
    // If the full list was already loaded by another page, reuse it —
    // no point firing a second request for a subset of the same data.
    // IEmailContent is a structural superset of MinimalEmail (id + name).
    if (this._emails) {
      return this._emails;
    }
    if (this.minimalInitialized) {
      return this._emailsMinimal;
    }
    this.minimalInitialized = true;
    getEmailsMinimal()
      .then((emails) => {
        const entries = _.map(emails, (email): [
          IEmailContent["id"],
          MinimalEmail
        ] => [email.id, email]);
        runInAction(() => {
          this._emailsMinimal = new Map(entries);
        });
      })
      .catch((e) => {
        toastError({
          message: "Error while loading your emails.",
          extra: e,
        });
      });
    return this._emailsMinimal;
  }

  /**
   * Note that we need to load metrics for emails
   */
  @action
  addEmailsToLoadMetricsFor(emailIds: IEmailContent["id"][]) {
    // figure out which email ids we need to load metrics for
    const emailsToLoad = emailIds.filter(
      (emailId) =>
        // Not loaded
        !this.allTimeMetricsByEmail[emailId] &&
        // not pending to load
        !this.emailIdsToLoadMetrics.includes(emailId)
    );
    runInAction(() => {
      this.emailIdsToLoadMetrics.push(...emailsToLoad);
    });
  }

  emailIdsToLoadMetrics: IEmailContent["id"][] = [];

  @observable
  allTimeMetricsByEmail: AllTimeMetricsByEmail = {};

  /**
   * Load metrics of emails that we have noted as needing to be loaded
   */
  @action
  async loadAllTimeMetricsByEmail() {
    // figure out which email ids we need to load metrics for
    const emailsToLoad = this.emailIdsToLoadMetrics.filter(
      // Make sure we havent loaded it before
      (emailId) => !this.allTimeMetricsByEmail[emailId]
    );
    if (emailsToLoad.length > 0) {
      try {
        const { stats: metrics } = await getEmailsMetrics({
          period: "allTime",
          startDate: new Date("2017-01-01T00:00:00.000Z"),
          groupByEmail: true,
          timezone:
            getUserTimezone() ||
            this.rootStore.accountStore.account?.timezone ||
            "UTC",
          emailIds: emailsToLoad,
        });

        // Placeholder for all retrieved emails
        let formatted = _.reduce(
          emailsToLoad,
          (acc, emailId) => {
            acc[emailId] = {};
            return acc;
          },
          {} as AllTimeMetricsByEmail
        );

        formatted = _.reduce(
          metrics,
          (acc, emailMetrics, emailId) => {
            acc[emailId] = emailMetrics;
            return acc;
          },
          formatted as AllTimeMetricsByEmail
        );

        runInAction(() => {
          // store the metrics
          _.merge(this.allTimeMetricsByEmail, observable(formatted));

          // remove all email ids that we have loaded metrics for
          this.emailIdsToLoadMetrics = this.emailIdsToLoadMetrics.filter(
            (emailId) => !emailsToLoad.includes(emailId)
          );
        });
      } catch (e) {
        // ignore error
        console.log(e);
        // Give the server some time to recover before trying again
        setTimeout(() => {
          this.loadAllTimeMetricsByEmail();
        }, 30000);
        return;
      }
    }
    // check again after a second
    setTimeout(() => {
      this.loadAllTimeMetricsByEmail();
    }, 1000);
  }

  @observable
  metricsCache: Dictionary<CachedMetrics> = {};

  /**
   * Tail of the in-flight write chain per email id.
   *
   * The editor has two independent writers for the same document: the
   * debounced auto-save (`PATCH /v1/emails/:id/versions/latest`) and the
   * manual Save / "Save and close" (`PATCH /v1/emails/:id`). Both send the
   * whole `editor` blob and the server applies them last-writer-wins, so an
   * auto-save that was issued from a pre-edit snapshot and happens to land
   * after the manual save silently reverts what the user just saved (reported
   * as "preheader text disappears after Save and close").
   *
   * Every write for an email goes through this chain, so a save is only sent
   * after the previous one has been acknowledged — i.e. after the server has
   * committed it. That makes the newest write the last writer.
   */
  private emailWriteChain: Map<
    IEmailContent["id"],
    Promise<unknown>
  > = new Map();

  /**
   * Don't let a stuck request block saving forever — if the previous write
   * hasn't been acknowledged by then, go ahead anyway rather than hang the UI.
   *
   * This bounds only this chain. An editor's save (updateEmail with
   * fromLoadedRevision, and autoSaveEmail) also waits in persistEmail for the
   * save of that email before it (oneSaveAtATime), with no give-up of its
   * own: sent beside it, it would carry the revision that save replaces and
   * be refused as changed elsewhere. Behind a stuck request such a save is
   * bounded by that request's timeout (axios' default, setAxiosDefaults),
   * not by this.
   */
  static readonly writeChainMaxWaitMs = 20000;

  /**
   * Queue `task` behind any write already in flight for this email.
   */
  private enqueueEmailWrite<T>(
    emailId: IEmailContent["id"] | undefined,
    task: () => Promise<T>
  ): Promise<T> {
    if (!emailId) {
      return task();
    }
    const previous = this.emailWriteChain.get(emailId);
    const waitForPrevious: Promise<unknown> = previous
      ? Promise.race([
          previous.catch(() => undefined),
          new Promise<void>((resolve) =>
            setTimeout(resolve, EmailsStore.writeChainMaxWaitMs)
          ),
        ])
      : Promise.resolve();
    const run = waitForPrevious.then(task);
    // Keep the chain alive even when a write rejects, and drop it once this
    // task is the last one queued so the map doesn't grow unbounded.
    const settled = run.catch(() => undefined);
    this.emailWriteChain.set(emailId, settled);
    settled.then(() => {
      if (this.emailWriteChain.get(emailId) === settled) {
        this.emailWriteChain.delete(emailId);
      }
    });
    return run;
  }

  @action
  async getFullEmail(
    id: IEmailContent["id"],
    /** fetched even when the store holds it: the editor reloading an email saved elsewhere */
    { fresh = false }: { fresh?: boolean } = {}
  ) {
    // check if the email has been populated before (and with the revision
    // an editor's saves are checked against, see persistEmail)
    const existingEmail =
      !fresh && loadedEmailRevision(id) ? this.getEmailById(id) : undefined;
    if (existingEmail) {
      // For Unlayer emails, we need editor.state to be a proper object
      // For richtext emails, we need editor.state to be a string
      // For visual emails, we need editor.html
      const hasValidEditorState =
        existingEmail.editor?.type === "unlayer" &&
        existingEmail.editor?.state &&
        typeof existingEmail.editor.state === "object" &&
        Object.keys(existingEmail.editor.state).length > 0;
      const hasValidRichTextState =
        existingEmail.editor?.type === "richtext" &&
        existingEmail.editor?.state &&
        typeof existingEmail.editor.state === "string" &&
        existingEmail.editor.state.length > 0;
      const hasValidVisualEditor =
        existingEmail.editor?.type === "visual" && existingEmail.editor?.html;
      const hasValidHtml = existingEmail.html && existingEmail.html.length > 0;

      // Only return cached email if it has complete data
      if (
        hasValidHtml &&
        (hasValidEditorState || hasValidRichTextState || hasValidVisualEditor)
      ) {
        return existingEmail;
      }
      // If cached email is incomplete, fall through to fetch full email
    }
    try {
      // Read BEFORE the request goes out. The record about to arrive describes
      // the email as it was at this moment, so this is the moment the write
      // count has to be compared against. Reading it after the await instead
      // counts any save that landed WHILE the fetch was in flight as if it had
      // happened first, the generations match, and the stale record is written
      // over the save - the exact clobber this guards.
      const generationAtRequest = this.writeGeneration.get(id) || 0;
      const fullEmail = await getEmail(id);
      // its verdict, unless a save answered with a newer one while it was in flight
      if ((this.writeGeneration.get(id) || 0) === generationAtRequest) {
        this.noteCanSpamVerdict(fullEmail);
      }
      // Caching still has to wait for the map - without it the full record is
      // dropped whenever the single fetch wins the race, and Broadcast Review
      // shows a blank Preheader row forever. The CALLER does not wait: it
      // already has the email it asked for, and the generation captured above
      // keeps the delayed write from landing on top of a newer save.
      void this.cacheFullEmail(fullEmail, generationAtRequest);
      return fullEmail;
    } catch (e) {
      // toaster error
      toastError({
        message: "Error while loading your email.",
        extra: e,
      });
      return undefined;
    }
  }

  @action
  async updateEmail(
    email: Partial<IEmailContent>,
    /** an editor's save: throws EmailChangedError if the email was saved elsewhere since it loaded it */
    options: { fromLoadedRevision?: boolean } = {}
  ) {
    try {
      if (!email.subject) {
        throw new Error("Email subject is needed.");
      }
      if (!email.fromEmail) {
        throw new Error("From email is needed.");
      }
      if (!email.id) {
        throw new Error("Missing email ID.");
      }
      if (!email.editor || !email.html) {
        throw new Error("Missing email content.");
      }

      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isSavingEmail = true;
      });
      // Wait for any auto-save already in flight for this email, so this
      // manual save is the last write the server applies.
      const updatedEmail = await this.enqueueEmailWrite(email.id, () =>
        updateEmail(email, options)
      );
      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isSavingEmail = false;
        // Counted even when the map does not exist yet to cache into - that is
        // exactly the case a waiting getFullEmail snapshot would overwrite.
        this.markWritten(updatedEmail.id);
        this.noteCanSpamVerdict(updatedEmail);
        // Update the email in the store to ensure reactivity
        // Delete and re-add to ensure MobX detects the change
        if (this.emails) {
          this.emails.delete(updatedEmail.id);
          this.emails.set(updatedEmail.id, updatedEmail);
        }
      });
      return updatedEmail;
    } catch (e) {
      // Disable loading
      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isSavingEmail = false;
      });
      // saved elsewhere since the editor loaded it: the editor reloads it
      if (isEmailChangedError(e)) throw e;
      // if we have an issue with liquid tags display a more helpful error
      if (e.message.includes("Couldn't process Liquid tags")) {
        this.rootStore.uiStore.emailEditorLiquidTagsError.setError(e.message);
        return undefined;
      }
      // toaster error
      toastError({
        message: "Couldn't save your email.",
        extra: e,
      });
      return undefined;
    }
  }

  /**
   * Save only these fields of an email (a broadcast's rename): the API
   * merges them into the email as it is (see persistEmail updateEmailFields),
   * never the store's whole copy, which an AI agent's edit may have made
   * stale. The store keeps the email the API answers with.
   */
  @action
  async updateEmailFields(
    emailId: IEmailContent["id"],
    fields: Partial<Omit<IEmailContent, "id">>
  ) {
    try {
      const updated = await updateEmailFields(emailId, fields);
      runInAction(() => {
        if (this.emails && updated) {
          this.emails.delete(updated.id);
          this.emails.set(updated.id, updated);
        }
      });
      return updated;
    } catch (e) {
      toastError({
        message: "Couldn't save your email.",
        extra: e,
      });
      return undefined;
    }
  }

  @action
  async autoSaveEmail(email: Partial<IEmailContent>) {
    try {
      // Relaxed validation for auto-save - don't require all fields
      if (!email.id) {
        throw new Error("Missing email ID for auto-save.");
      }

      // Auto-save with minimal validation
      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isAutoSavingEmail = true;
      });
      const result = await this.enqueueEmailWrite(email.id, () =>
        updateEmailLatestVersion(email)
      );
      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isAutoSavingEmail = false;
        this.markWritten(result.email.id);
        this.noteCanSpamVerdict(result.email);
        // Update local state with the saved email
        // Delete and re-add to ensure MobX detects the change
        if (this.emails) {
          this.emails.delete(result.email.id);
          this.emails.set(result.email.id, result.email);
        }
      });
      return result;
    } catch (e) {
      // Disable loading
      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isAutoSavingEmail = false;
      });
      // saved elsewhere since the editor loaded it: the editor reloads it
      if (isEmailChangedError(e)) throw e;
      // Don't show error toast for auto-save failures to avoid spam
      console.warn("Auto-save failed:", e.message);
      return undefined;
    }
  }

  @action
  async applyVersion(emailId: number, version: number) {
    try {
      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isSavingEmail = true;
      });

      const result = await this.enqueueEmailWrite(emailId, () =>
        applyEmailVersion(emailId, version)
      );

      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isSavingEmail = false;
        this.markWritten(result.email.id);
        this.noteCanSpamVerdict(result.email);
        // Update local state with the restored email
        this.emails?.set(result.email.id, result.email);
      });

      return result;
    } catch (e) {
      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isSavingEmail = false;
      });

      toastError({
        message: "Couldn't apply this version.",
        extra: e,
      });
      return undefined;
    }
  }

  @action
  async createEmail(email: Partial<IEmailContent>) {
    try {
      if (!email.subject) {
        throw new Error("Email subject is needed.");
      }
      if (!email.fromEmail) {
        throw new Error("From email is needed.");
      }
      if (!email.editor || !email.html) {
        throw new Error("Missing email content.");
      }
      // Merge some defaults with the email we got from the editor
      const createableEmail: EmailContentCreate = _.merge(
        {
          type: "HTML",
          name: "Unnamed email",
        },
        email as Overwrite<
          Partial<IEmailContent>,
          {
            subject: IEmailContent["subject"];
            fromEmail: IEmailContent["fromEmail"];
          }
        >
      );

      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isSavingEmail = true;
      });
      const currentFolderId = this.rootStore.foldersStore.getSelectedFolder(
        "emails"
      );
      const newEmail = await createEmail(createableEmail);
      runInAction(() => {
        this.markWritten(newEmail.id);
        this.noteCanSpamVerdict(newEmail);
        this.rootStore.uiStore.emailEditor.isSavingEmail = false;

        // add newly created email to current folder
        if (currentFolderId) {
          this.rootStore.foldersStore.pushItemToFolder({
            folderId: currentFolderId,
            itemId: newEmail.id,
            type: "emails",
          });
        }
      });
      // Same trap as getFullEmail: while the list is still in flight the map
      // does not exist yet, so a newly created email was dropped here and never
      // appeared in the list. Caching waits for the map; the editor does not,
      // or it keeps showing "Saving..." after the server has already created
      // the email. Neither the flag nor the folder needs the map.
      void this.cacheFullEmail(
        newEmail,
        this.writeGeneration.get(newEmail.id) || 0
      );
      return newEmail;
    } catch (e) {
      // Disable loading
      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isSavingEmail = false;
      });
      // if we have an issue with liquid tags display a more helpful error
      if (e.message.includes("Couldn't process Liquid tags")) {
        this.rootStore.uiStore.emailEditorLiquidTagsError.setError(e.message);
        return undefined;
      }
      // rethrow for toaster errors
      toastError({
        message: `Error while creating your email.`,
        extra: e,
      });
      return undefined;
    }
  }

  @action
  async duplicateEmail(
    emailId: IEmailContent["id"],
    emailFields: Partial<IEmailContent> = {
      isStandalone: true,
    }
  ) {
    // the email as it is saved now, never the store's copy: an AI agent's
    // edit since this tab loaded it was missing from the copy
    const emailToDuplicate = await this.getFullEmail(emailId, { fresh: true });
    if (!emailToDuplicate) return;
    const newEmail = _.cloneDeep(_.omit(emailToDuplicate, "id", "stats"));
    newEmail.name = `Copy of ${newEmail.name}`;
    this.rootStore.uiStore.emailDuplication.startLoading();
    try {
      return await this.createEmail({
        ...newEmail,
        ...emailFields,
      });
    } finally {
      this.rootStore.uiStore.emailDuplication.stopLoading();
    }
  }

  @action
  async copyEmailToAnotherAccount({
    emailId,
    targetAccountId,
  }: {
    emailId: IEmailContent["id"];
    targetAccountId: IAccount["id"];
  }) {
    this.rootStore.uiStore.emailDuplication.startLoading();
    try {
      await copyEmailToAnotherAccount({
        emailId,
        targetAccountId,
      });
      toastSuccess("✅ Email copied.");
    } catch (e) {
      toastError({
        message: `Error while copying email. ${(e as Error).message}`,
        extra: e,
      });
    } finally {
      this.rootStore.uiStore.emailDuplication.stopLoading();
    }
  }

  @action
  async archiveEmail(emailId: IEmailContent["id"]) {
    if (!this.emails) return;
    const emailToArchive = this.emails.get(emailId);
    if (!emailToArchive) {
      return;
    }
    try {
      // Delete from the local store
      this.emails.delete(emailId);
      emailToArchive.archived = true;
      // only the flag: the store's copy of the rest may be stale (an AI
      // agent's edit since it was loaded), and sent whole it put that back
      return await updateEmailFields(emailId, { archived: true });
    } catch (e) {
      // rethrow for toaster errors
      toastError({
        message: `Error while archiving email. ${(e as Error).message}`,
        extra: e,
      });
      return undefined;
    }
  }

  @action
  async sendTestEmail(email: Partial<IEmailContent>, sendTo?: string) {
    try {
      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isSendingTestEmail = true;
      });
      const receiverEmail =
        sendTo || this.rootStore.accountStore?.account?.email;
      const result = await sendTestEmail(email, receiverEmail!);
      if (result?.notSent?.length) {
        // Sent to some addresses and not others - the server's message says which.
        toastError(result.message);
      } else {
        toastSuccess(
          receiverEmail
            ? `Test email sent to ${receiverEmail} ✉️`
            : "Test email sent ️️️✉️"
        );
      }
    } catch (e) {
      runInAction(() => {
        this.rootStore.uiStore.emailEditor.isSendingTestEmail = false;
      });
      // toaster msg
      toastError({
        message: "Error while sending test email.",
        extra: e,
      });
    }
    runInAction(() => {
      this.rootStore.uiStore.emailEditor.isSendingTestEmail = false;
    });
  }

  getCachedMetrics(props: {
    metricsPeriod: IStatsPeriod;
    startDate?: Date;
    endDate?: Date;
    flowIds?: IIntegration["id"][];
    emailIds?: IEmailContent["id"][];
    broadcastId?: Broadcast["id"];
  }) {
    return this.metricsCache[JSON.stringify(props)];
  }

  @action
  clearCachedMetrics(props: {
    metricsPeriod: IStatsPeriod;
    startDate?: Date;
    endDate?: Date;
    flowIds?: IIntegration["id"][];
    emailIds?: IEmailContent["id"][];
    broadcastId?: Broadcast["id"];
  }) {
    delete this.metricsCache[JSON.stringify(props)];
  }

  // Use this to keep track of which metrics are still loading
  // So that we don't trigger a new request on them
  retrivingMetricFor: Parameters<typeof getEmailsMetrics>[0] | undefined;

  @action
  async getEmailMetrics({
    metricsPeriod,
    startDate,
    endDate,
    flowIds,
    emailIds,
    broadcastId,
  }: {
    metricsPeriod: IStatsPeriod;
    startDate?: Date;
    endDate?: Date;
    flowIds?: IIntegration["id"][];
    emailIds?: IEmailContent["id"][];
    broadcastId?: Broadcast["id"];
  }) {
    // Make sure we are not refreshing the same metrics if they haven't loaded yet
    const metricOptions = {
      period: metricsPeriod,
      startDate,
      endDate,
      flowIds,
      emailIds,
      broadcastId,
      timezone:
        getUserTimezone() ||
        this.rootStore.accountStore.account?.timezone ||
        "UTC",
    };
    if (_.isEqual(this.retrivingMetricFor, metricOptions)) {
      // we are already loading these metris
      return;
    } else {
      // Loading new metrics, so store them to prevent reloading of the same ones
      this.retrivingMetricFor = metricOptions;
    }

    this.rootStore.uiStore.emailMetrics.startLoading();
    try {
      const { stats: metrics, clicks } = await getEmailsMetrics(metricOptions);

      // Lets sum the individual email metrics into single metric for the whole
      // account

      // Sum array of arrays by columns.
      // E.g [[1,2], [3,4]] to [4, 6]
      const sumColumns = (data: any[][]) => {
        return _.map(_.unzip(data), _.sum);
      };

      const summedMetrics = _.reduce(
        _.omit(metrics, "periods"),
        (acc, emailMetrics) => {
          _.each(emailMetrics, (metric, metricKey) => {
            if (!acc[metricKey]) {
              acc[metricKey] = metric;
            } else {
              acc[metricKey] = sumColumns([acc[metricKey], metric]);
            }
          });
          return acc;
        },
        {} as ISingleEmailStats
      );
      const periods = metrics.periods;

      const result = { metrics: summedMetrics, periods, clicks };
      // save metrics in cache
      runInAction(() => {
        this.metricsCache[
          JSON.stringify({
            metricsPeriod,
            startDate,
            endDate,
            flowIds,
            emailIds,
            broadcastId,
          })
        ] = result;
      });
      return result;
    } catch (e) {
      toastError({
        message: "Error while getting email stats.",
        extra: e,
      });
    } finally {
      this.retrivingMetricFor = undefined;
      this.rootStore.uiStore.emailMetrics.stopLoading();
    }
    return;
  }

  @action
  async exportEmailEvents({
    startDate,
    endDate,
    flowIds,
    emailIds,
    type,
  }: {
    startDate?: Date;
    endDate?: Date;
    flowIds?: IIntegration["id"][];
    emailIds?: IEmailContent["id"][];
    type: IEmailStatsType;
  }) {
    try {
      const email = this?.rootStore?.accountStore?.account?.email;
      if (!email) {
        throw new Error(
          "Can't send exported people, since you have no email set."
        );
      }

      await exportEmailEvents({
        startDate,
        endDate,
        flowIds,
        emailIds,
        type,
        notifyEmail: email,
      });
      toastSuccess(`✅ The export will be emailed to ${email} shortly.`);
    } catch (e) {
      toastError({
        message: "Error while exporting people.",
        extra: e,
      });
    }
  }

  @action
  async getBroadcastABTestMetrics({
    metricsPeriod,
    startDate,
    endDate,
    flowIds,
    emailIds,
    broadcastId,
  }: {
    metricsPeriod: IStatsPeriod;
    startDate?: Date;
    endDate?: Date;
    flowIds?: IIntegration["id"][];
    emailIds?: IEmailContent["id"][];
    broadcastId?: Broadcast["id"];
  }) {
    this.rootStore.uiStore.emailMetrics.startLoading();
    try {
      const { stats: metrics } = await getEmailsMetrics({
        period: metricsPeriod,
        startDate,
        endDate,
        flowIds,
        emailIds,
        broadcastId,
        abTest: true,
        timezone:
          getUserTimezone() ||
          this.rootStore.accountStore.account?.timezone ||
          "UTC",
      });

      return metrics;
    } catch (e) {
      toastError({
        message: "Error while getting email stats.",
        extra: e,
      });
    } finally {
      this.rootStore.uiStore.emailMetrics.stopLoading();
    }
    return;
  }

  @action
  async getBroadcastsMetrics({
    metricsPeriod,
    startDate,
    broadcastIds,
    groupByBroadcast,
  }: {
    broadcastIds?: Broadcast["id"][];
    groupByBroadcast: boolean;
    startDate: Date;
    metricsPeriod: IStatsPeriod;
  }) {
    try {
      const { stats: metrics } = await getEmailsMetrics({
        period: metricsPeriod,
        startDate,
        broadcastIds,
        groupByBroadcast,
        timezone:
          getUserTimezone() ||
          this.rootStore.accountStore.account?.timezone ||
          "UTC",
      });

      return metrics;
    } catch (e) {
      toastError({
        message: "Error while getting broadcast stats.",
        extra: e,
      });
    }
    return;
  }

  /**
   * Check if email is used in flows
   */
  isEmailUsedInFlows(
    emailIds: IEmailContent["id"][],
    flows: IFlow[],
    checkDeactivatedFlows?: boolean
  ) {
    // TODO - Flow paging: move this to backend
    // Go through each step in each flow and check if the email is used in it
    // trigger getting flows

    const isUsed = _.reduce(
      emailIds,
      (acc, emailId) => {
        acc[emailId] = false;
        return acc;
      },
      {} as Dictionary<boolean>
    );

    // check if email is used in flows
    _.each(flows, (flow) => {
      if (!checkDeactivatedFlows && flow.status !== integrationStateActive) {
        return;
      }
      for (const stepKey in flow.steps) {
        const step = flow.steps[stepKey];
        // We are only checking steps that are email related
        if (
          step.operationKey === "/send" ||
          step.operationKey === "/email-activity" ||
          step.operationKey === "/email-trigger" ||
          step.operationKey === "/email-trigger-v2"
        ) {
          // find the email used in this step
          const stepFieldsValues = StepBase.getInputFieldsValues(step);
          // Is the email id in used in the step same as current email id
          if (emailIds.indexOf(Number(stepFieldsValues.emailId)) !== -1) {
            isUsed[stepFieldsValues.emailId] = true;
          }
          if (emailIds.indexOf(Number(stepFieldsValues?.email?.id)) !== -1) {
            isUsed[stepFieldsValues?.email?.id] = true;
          }
        }
      }
    });

    return isUsed;
  }
}
